import React, { useEffect, useState, useRef } from 'react';
import { courseAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiSearch, FiList, FiDollarSign, FiX, FiUser } from 'react-icons/fi';

const AllowCourses = () => {
  const [tradegroups, setTradegroups] = useState([]);
  const [trades, setTrades] = useState([]);
  const [courses, setCourses] = useState([]);
  
  const [selectedTradegroup, setSelectedTradegroup] = useState('');
  const [selectedTrade, setSelectedTrade] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // Search states
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [studentSearchResults, setStudentSearchResults] = useState([]);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  
  const searchTimeoutRef = useRef(null);
  const searchDropdownRef = useRef(null);
  
  const [paymentData, setPaymentData] = useState({
    amount: '',
    discount: '0',
    payment_mode_fee: 'cash',
    fee_note: ''
  });

  useEffect(() => {
    fetchTradegroups();
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setShowStudentDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTradegroups = async () => {
    try {
      const response = await courseAPI.getAllowCoursesTradegroups();
      if (response.data.success) {
        setTradegroups(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tradegroups:', error);
      toast.error('Error fetching tradegroups');
    }
  };

  const handleTradegroupChange = async (e) => {
    const tradegroupId = e.target.value;
    setSelectedTradegroup(tradegroupId);
    setSelectedTrade('');
    setTrades([]);
    setSelectedStudent(null);
    setStudentSearchTerm('');
    setStudentSearchResults([]);
    setCourses([]);
    
    if (!tradegroupId || tradegroupId === 'all') return;
    
    try {
      const response = await courseAPI.getAllowCoursesTrades(tradegroupId);
      if (response.data.success) {
        setTrades(response.data.data);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error fetching trades');
    }
  };

  const handleTradeChange = (e) => {
    const tradeId = e.target.value;
    setSelectedTrade(tradeId);
    setSelectedStudent(null);
    setStudentSearchTerm('');
    setStudentSearchResults([]);
    setCourses([]);
  };

  // Debounced student search
  const handleStudentSearch = (value) => {
    setStudentSearchTerm(value);
    
    if (value.length < 2) {
      setStudentSearchResults([]);
      setShowStudentDropdown(false);
      return;
    }
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await courseAPI.searchAllowCoursesStudents(
          value,
          selectedTradegroup,
          selectedTrade,
          20
        );
        if (response.data.success) {
          setStudentSearchResults(response.data.data);
          setShowStudentDropdown(true);
        }
      } catch (error) {
        console.error('Error searching students:', error);
        toast.error('Error searching students');
      } finally {
        setIsSearching(false);
      }
    }, 300); // Wait 300ms after user stops typing
  };

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setStudentSearchTerm(`${student.firstname} ${student.lastname} - ${student.email}`);
    setShowStudentDropdown(false);
    setCourses([]);
  };

  const handleClearStudent = () => {
    setSelectedStudent(null);
    setStudentSearchTerm('');
    setStudentSearchResults([]);
    setCourses([]);
  };

  const handleSearch = async () => {
    if (!selectedStudent) {
      toast.error('Please search and select a student first');
      return;
    }
    
    setLoading(true);
    try {
      const response = await courseAPI.searchStudentCourses(selectedStudent.id);
      if (response.data.success) {
        setCourses(response.data.data);
      }
    } catch (error) {
      console.error('Error searching courses:', error);
      toast.error('Error searching courses');
    } finally {
      setLoading(false);
    }
  };

  const handlePayClick = (course) => {
    if (!selectedStudent) {
      toast.error('Please select a student first');
      return;
    }
    
    setSelectedCourse(course);
    setPaymentData({
      amount: course.current_price.toFixed(2),
      discount: '0',
      payment_mode_fee: 'cash',
      fee_note: ''
    });
    setShowPaymentModal(true);
  };

  const handleUnpaidClick = async (courseId, email) => {
    if (!window.confirm('Are you sure you want to mark this course as unpaid?')) {
      return;
    }
    
    try {
      const response = await courseAPI.unpaidCourse(courseId, email);
      if (response.data.success) {
        toast.success('Course unpaid successfully');
        handleSearch(); // Refresh the course list
      }
    } catch (error) {
      console.error('Error unpaiding course:', error);
      toast.error(error.response?.data?.message || 'Error unpaiding course');
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await courseAPI.allowCourse({
    
        courseid: selectedCourse.id,
        student_id: selectedStudent.id,
        amount: paymentData.amount,
        discount: paymentData.discount,
        payment_mode_fee: paymentData.payment_mode_fee,
        fee_note: paymentData.fee_note
      });
      
      if (response.data.success) {
        toast.success('Course allowed successfully!');
        setShowPaymentModal(false);
        setSelectedCourse(null);
        setPaymentData({
          amount: '',
          discount: '0',
          payment_mode_fee: 'cash',
          fee_note: ''
        });
        handleSearch(); // Refresh the course list
      }
    } catch (error) {
      console.error('Error allowing course:', error);
      toast.error(error.response?.data?.message || 'Error allowing course');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <FiDollarSign /> Offline Payment for Online Students
        </h1>
        <p className="text-gray-600">Manually assign courses to online students with offline payment</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Trade Group */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trade Group <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedTradegroup}
              onChange={handleTradegroupChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              <option value="">Select Trade Group</option>
              <option value="all">All Students</option>
              {tradegroups.map((tg) => (
                <option key={tg.id} value={tg.id}>
                  {tg.name}
                </option>
              ))}
            </select>
          </div>

          {/* Trade */}
          {selectedTradegroup && selectedTradegroup !== 'all' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trade
              </label>
              <select
                value={selectedTrade}
                onChange={handleTradeChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                <option value="">All Trades</option>
                {trades.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Student Search */}
          <div className="relative" ref={searchDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Student <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={studentSearchTerm}
                onChange={(e) => handleStudentSearch(e.target.value)}
                onFocus={() => {
                  if (studentSearchResults.length > 0) {
                    setShowStudentDropdown(true);
                  }
                }}
                placeholder="Search by name, email, mobile..."
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                disabled={!selectedTradegroup}
              />
              {selectedStudent && (
                <button
                  onClick={handleClearStudent}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FiX />
                </button>
              )}
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
            
            {/* Search Results Dropdown */}
            {showStudentDropdown && studentSearchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {studentSearchResults.map((student) => (
                  <div
                    key={student.id}
                    onClick={() => handleSelectStudent(student)}
                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-center gap-2">
                      <FiUser className="text-gray-400" />
                      <div>
                        <div className="font-medium text-gray-900">
                          {student.firstname} {student.lastname}
                        </div>
                        <div className="text-sm text-gray-600">{student.email}</div>
                        <div className="text-xs text-gray-500">{student.mobileno}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* No results message */}
            {showStudentDropdown && studentSearchTerm.length >= 2 && studentSearchResults.length === 0 && !isSearching && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-center text-gray-500">
                No students found
              </div>
            )}
            
            {/* Hint message */}
            {studentSearchTerm.length > 0 && studentSearchTerm.length < 2 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-center text-sm text-gray-500">
                Type at least 2 characters to search
              </div>
            )}
          </div>

          {/* Search Button */}
          {selectedStudent && (
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                disabled={loading}
                className="w-full px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <FiSearch /> {loading ? 'Searching...' : 'Search Courses'}
              </button>
            </div>
          )}
        </div>
        
        {!selectedTradegroup && (
          <div className="mt-4 text-sm text-gray-500">
            Please select a Trade Group to search for students
          </div>
        )}
      </div>

      {/* Courses Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiList /> Course List
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Course
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  TradeGroup
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price (₹)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Price (₹)
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {courses.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    {selectedStudent ? 'Click "Search Courses" button to load courses' : 'Please search and select a student first'}
                  </td>
                </tr>
              ) : (
                courses.map((course) => (
                  <tr key={course.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{course.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{course.tradegroup}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{course.trade}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {course.price.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-green-600">
                        {course.current_price.toFixed(2)}
                        {course.discount > 0 && (
                          <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                            {course.discount}% OFF
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {course.is_paid ? (
                        <button
                          onClick={() => handleUnpaidClick(course.id, selectedStudent.email)}
                          className="inline-flex items-center px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors"
                        >
                          <FiDollarSign className="mr-1" /> Paid
                        </button>
                      ) : (
                        <button
                          onClick={() => handlePayClick(course)}
                          className="inline-flex items-center px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded transition-colors"
                        >
                          <FiDollarSign className="mr-1" /> Pay
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">Online Course Fee</h2>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handlePaymentSubmit} className="p-6">
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Course Name:</p>
                <p className="text-base font-semibold text-gray-900">{selectedCourse.title}</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pay Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  disabled={true}
                  step="any"
                  min="0"
                  value={paymentData.amount}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (value >= 0 || e.target.value === '') {
                      setPaymentData({ ...paymentData, amount: e.target.value });
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent minus sign
                    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                      e.preventDefault();
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  required
                />
              </div>

              {/* <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount
                </label>
                <input
                  type="number"
                  step="any"
                  min="0"
                disabled={true}
                  value={paymentData.discount}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if ((value >= 0 && value <= 100) || e.target.value === '') {
                      setPaymentData({ ...paymentData, discount: e.target.value });
                    }
                  }}
                  onKeyDown={(e) => {
                    // Prevent minus sign
                    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                      e.preventDefault();
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div> */}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Mode
                </label>
                <div className="space-y-2">
                  {['cash', 'cheque', 'dd', 'bank_transfer', 'upi', 'card'].map((mode) => (
                    <label key={mode} className="flex items-center">
                      <input
                        type="radio"
                        name="payment_mode_fee"
                        value={mode}
                        checked={paymentData.payment_mode_fee === mode}
                        onChange={(e) => setPaymentData({ ...paymentData, payment_mode_fee: e.target.value })}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 capitalize">
                        {mode.replace('_', ' ')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note
                </label>
                <textarea
                  value={paymentData.fee_note}
                  onChange={(e) => setPaymentData({ ...paymentData, fee_note: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  rows="3"
                  placeholder="Add any notes..."
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-4">
                <span className="text-lg font-semibold text-gray-900">Total Pay</span>
                <span className="text-2xl font-bold text-green-600">₹{paymentData.amount}</span>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:bg-gray-400"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Pay'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllowCourses;
